import React, { useState } from 'react';
import { X, Filter, ArrowUpDown, Copy, Type, Calculator, BarChart3, Search, Droplets, Columns, Combine, Mail, TableProperties, SplitSquareHorizontal, Link2, Sparkles, RefreshCw, Layers, GitCompare, Tags } from 'lucide-react';
import FilterOperation from './operations/FilterOperation';
import SortOperation from './operations/SortOperation';
import MergeOperation from './operations/MergeOperation';
import AggregateOperation from './operations/AggregateOperation';
import CalculatedColumnOperation from './operations/CalculatedColumnOperation';
import RenameColumnsOperation from './operations/RenameColumnsOperation';
import SelectColumnsOperation from './operations/SelectColumnsOperation';
import ReplaceOperation from './operations/ReplaceOperation';
import HandleMissingOperation from './operations/HandleMissingOperation';
import RemoveDuplicatesOperation from './operations/RemoveDuplicatesOperation';
import EmailCleanOperation from './operations/EmailCleanOperation';
import PivotTableOperation from './operations/PivotTableOperation';
import SplitColumnOperation from './operations/SplitColumnOperation';
import ConcatColumnsOperation from './operations/ConcatColumnsOperation';
import ConvertTypeOperation from './operations/ConvertTypeOperation';
import FuzzySearchOperation from './operations/FuzzySearchOperation';
import AppendOperation from './operations/AppendOperation';
import CompareOperation from './operations/CompareOperation';
import RecodeOperation from './operations/RecodeOperation';
import OperationExample from './OperationExample';
import { useT } from '../i18n';

// Le libelle et la description sont des cles, pas du texte : c'est ce qui rend
// la liste traduisible sans dupliquer la structure par langue.
const OPERATIONS = [
  { id: 'filter', nameKey: 'op.filter.name', icon: Filter, descriptionKey: 'op.filter.description', category: 'basic' },
  { id: 'sort', nameKey: 'op.sort.name', icon: ArrowUpDown, descriptionKey: 'op.sort.description', category: 'basic' },
  { id: 'select', nameKey: 'op.select.name', icon: Columns, descriptionKey: 'op.select.description', category: 'basic' },
  { id: 'rename', nameKey: 'op.rename.name', icon: Type, descriptionKey: 'op.rename.description', category: 'basic' },
  
  { id: 'merge', nameKey: 'op.merge.name', icon: Combine, descriptionKey: 'op.merge.description', category: 'advanced' },
  { id: 'append', nameKey: 'op.append.name', icon: Layers, descriptionKey: 'op.append.description', category: 'advanced' },
  { id: 'compare', nameKey: 'op.compare.name', icon: GitCompare, descriptionKey: 'op.compare.description', category: 'advanced' },
  { id: 'pivot', nameKey: 'op.pivot.name', icon: TableProperties, descriptionKey: 'op.pivot.description', category: 'advanced' },
  { id: 'aggregate', nameKey: 'op.aggregate.name', icon: BarChart3, descriptionKey: 'op.aggregate.description', category: 'advanced' },
  { id: 'calculated', nameKey: 'op.calculated.name', icon: Calculator, descriptionKey: 'op.calculated.description', category: 'advanced' },
  
  { id: 'split', nameKey: 'op.split.name', icon: SplitSquareHorizontal, descriptionKey: 'op.split.description', category: 'transform' },
  { id: 'concat', nameKey: 'op.concat.name', icon: Link2, descriptionKey: 'op.concat.description', category: 'transform' },
  { id: 'convert', nameKey: 'op.convert.name', icon: RefreshCw, descriptionKey: 'op.convert.description', category: 'transform' },
  { id: 'replace', nameKey: 'op.replace.name', icon: Search, descriptionKey: 'op.replace.description', category: 'transform' },
  { id: 'recode', nameKey: 'op.recode.name', icon: Tags, descriptionKey: 'op.recode.description', category: 'transform' },
  
  { id: 'duplicates', nameKey: 'op.duplicates.name', icon: Copy, descriptionKey: 'op.duplicates.description', category: 'clean' },
  { id: 'fuzzy', nameKey: 'op.fuzzy.name', icon: Sparkles, descriptionKey: 'op.fuzzy.description', category: 'clean' },
  { id: 'missing', nameKey: 'op.missing.name', icon: Droplets, descriptionKey: 'op.missing.description', category: 'clean' },
  { id: 'email-clean', nameKey: 'op.emailClean.name', icon: Mail, descriptionKey: 'op.emailClean.description', category: 'clean' },
];

const CATEGORIES = [
  { id: 'all', nameKey: 'category.all' },
  { id: 'basic', nameKey: 'category.basic' },
  { id: 'advanced', nameKey: 'category.advanced' },
  { id: 'transform', nameKey: 'category.transform' },
  { id: 'clean', nameKey: 'category.clean' },
];

const OperationSelector = ({ onClose, onOperationApplied }) => {
  const t = useT();
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredOperations = selectedCategory === 'all' 
    ? OPERATIONS 
    : OPERATIONS.filter(op => op.category === selectedCategory);

  const renderOperationForm = () => {
    const props = { onSuccess: onOperationApplied, onCancel: () => setSelectedOperation(null) };
    
    switch (selectedOperation) {
      case 'filter':
        return <FilterOperation {...props} />;
      case 'sort':
        return <SortOperation {...props} />;
      case 'merge':
        return <MergeOperation {...props} />;
      case 'append':
        return <AppendOperation {...props} />;
      case 'compare':
        return <CompareOperation {...props} />;
      case 'aggregate':
        return <AggregateOperation {...props} />;
      case 'calculated':
        return <CalculatedColumnOperation {...props} />;
      case 'rename':
        return <RenameColumnsOperation {...props} />;
      case 'select':
        return <SelectColumnsOperation {...props} />;
      case 'replace':
        return <ReplaceOperation {...props} />;
      case 'recode':
        return <RecodeOperation {...props} />;
      case 'missing':
        return <HandleMissingOperation {...props} />;
      case 'duplicates':
        return <RemoveDuplicatesOperation {...props} />;
      case 'email-clean':
        return <EmailCleanOperation {...props} />;
      case 'pivot':
        return <PivotTableOperation {...props} />;
      case 'split':
        return <SplitColumnOperation {...props} />;
      case 'concat':
        return <ConcatColumnsOperation {...props} />;
      case 'convert':
        return <ConvertTypeOperation {...props} />;
      case 'fuzzy':
        return <FuzzySearchOperation {...props} />;
      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="operation-modal">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {selectedOperation && (
                <button 
                  onClick={() => setSelectedOperation(null)} 
                  className="btn-ghost h-8 px-2 text-sm"
                  data-testid="back-to-operations"
                >
                  ← {t('operations.back')}
                </button>
              )}
              <h2 className="text-xl font-semibold">
                {selectedOperation
                  ? t(OPERATIONS.find(op => op.id === selectedOperation)?.nameKey)
                  : t('operations.selectTitle')}
              </h2>
              {selectedOperation && (
                <OperationExample
                  operationId={selectedOperation}
                  operationName={t(OPERATIONS.find(op => op.id === selectedOperation)?.nameKey)}
                  variant="panel"
                />
              )}
            </div>
            <button onClick={onClose} className="btn-ghost h-8 w-8 p-0" data-testid="close-modal" aria-label={t('operations.close')}>
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          {!selectedOperation ? (
            <>
              {/* Category tabs */}
              <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-border">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === cat.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    {t(cat.nameKey)}
                  </button>
                ))}
              </div>
              
              {/* Operations grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
                {filteredOperations.map((op) => {
                  const Icon = op.icon;
                  return (
                    // A card, not a button: the example link lives inside it,
                    // and a button nested in a button is invalid HTML — the
                    // inner one stops being clickable in some browsers.
                    <div
                      key={op.id}
                      className="card p-4 hover:border-primary transition-all duration-200 hover:shadow-md group"
                    >
                      <button
                        onClick={() => setSelectedOperation(op.id)}
                        className="w-full text-left"
                        data-testid={`operation-${op.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                            <Icon className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">{t(op.nameKey)}</h3>
                            <p className="text-xs text-muted-foreground">{t(op.descriptionKey)}</p>
                          </div>
                        </div>
                      </button>
                      <div className="mt-2 ml-[52px]">
                        <OperationExample operationId={op.id} operationName={t(op.nameKey)} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div>
              {renderOperationForm()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperationSelector;
